import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faCompress,
  faMinus,
  faPlus,
} from '@fortawesome/free-solid-svg-icons'
// The webpack config (craco.config.js) emits this file as a plain asset and gives
// us its URL, so the worker is served from our domain and matches the library.
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs'
import './PdfReader.css'

if (typeof workerUrl === 'string') {
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
} else {
  // Happens when the dev server was started before craco.config.js was changed.
  console.error(
    'PDF reader: the pdf.js worker was not bundled as a file. Restart the dev server.',
  )
}

// Fonts and character maps are only needed for PDFs that don't embed their fonts
// (or use CJK encodings). Without them pdf.js falls back to system fonts.
const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  isEvalSupported: false,
}

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3]
const MAX_FIT_WIDTH = 1000
const SIDE_PADDING = 16
const PAGE_GAP = 16
// Big canvases on high-density phones use a lot of memory; 2x is sharp enough.
const DEVICE_PIXEL_RATIO = Math.min(2, window.devicePixelRatio || 1)

/**
 * On localhost the file host may not send CORS headers, so the dev server fetches
 * the PDF for us (src/setupProxy.js). Production builds always use the real URL.
 */
export const getReadablePdfUrl = (url) => {
  if (process.env.NODE_ENV !== 'development' || !url) return url
  try {
    const parsed = new URL(url, window.location.href)
    if (parsed.origin === window.location.origin) return url
    return `/__pdf-proxy?url=${encodeURIComponent(parsed.href)}`
  } catch (e) {
    return url
  }
}

/** Renders a page only while it is near the visible area, to keep memory low. */
function PageSlot({ pageNumber, width, height, scrollRoot, registerSlot }) {
  const slotRef = useRef(null)
  const [isNearView, setIsNearView] = useState(pageNumber <= 2)

  useEffect(() => {
    registerSlot(pageNumber, slotRef.current)
    return () => registerSlot(pageNumber, null)
  }, [pageNumber, registerSlot])

  useEffect(() => {
    const slot = slotRef.current
    if (!slot || !scrollRoot || typeof IntersectionObserver === 'undefined') {
      setIsNearView(true)
      return undefined
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsNearView(entry.isIntersecting),
      { root: scrollRoot, rootMargin: '150% 0px' },
    )
    observer.observe(slot)
    return () => observer.disconnect()
  }, [scrollRoot])

  return (
    <div
      ref={slotRef}
      className='pdf-reader__page'
      style={{ width, minHeight: height, marginBottom: PAGE_GAP }}
    >
      {isNearView && (
        <Page
          pageNumber={pageNumber}
          width={width}
          devicePixelRatio={DEVICE_PIXEL_RATIO}
          renderTextLayer={false}
          renderAnnotationLayer
          loading={<div style={{ height }} />}
        />
      )}
    </div>
  )
}

/**
 * PDF reader drawn by pdf.js inside our app. It has no download or print buttons
 * in any browser, and looks the same on desktop and phones.
 */
export default function PdfReader({ url, title }) {
  const scrollRef = useRef(null)
  const slotsRef = useRef(new Map())
  const scrollFractionRef = useRef(null)

  const [scrollRoot, setScrollRoot] = useState(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [numPages, setNumPages] = useState(0)
  const [pageRatio, setPageRatio] = useState(1.414)
  const [zoomIndex, setZoomIndex] = useState(ZOOM_STEPS.indexOf(1))
  const [currentPage, setCurrentPage] = useState(1)
  const [reloadKey, setReloadKey] = useState(0)

  const readableUrl = getReadablePdfUrl(url)
  const zoom = ZOOM_STEPS[zoomIndex]

  // Start from the top whenever another file is opened.
  useEffect(() => {
    setNumPages(0)
    setCurrentPage(1)
    setZoomIndex(ZOOM_STEPS.indexOf(1))
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [readableUrl])

  const setScrollElement = useCallback((element) => {
    scrollRef.current = element
    setScrollRoot(element)
  }, [])

  useEffect(() => {
    const element = scrollRoot
    if (!element) return undefined
    const measure = () => setContainerWidth(element.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [scrollRoot])

  const fitWidth = Math.max(
    200,
    Math.min(containerWidth - SIDE_PADDING * 2, MAX_FIT_WIDTH),
  )
  const pageWidth = Math.round(fitWidth * zoom)
  const pageHeight = Math.round(pageWidth * pageRatio)

  // Keep the same spot in the document when zooming.
  const changeZoom = (nextIndex) => {
    const element = scrollRef.current
    if (element && element.scrollHeight > 0) {
      scrollFractionRef.current = element.scrollTop / element.scrollHeight
    }
    setZoomIndex(Math.max(0, Math.min(ZOOM_STEPS.length - 1, nextIndex)))
  }

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (element && scrollFractionRef.current !== null) {
      element.scrollTop = scrollFractionRef.current * element.scrollHeight
      scrollFractionRef.current = null
    }
  }, [pageWidth])

  const registerSlot = useCallback((pageNumber, element) => {
    if (element) slotsRef.current.set(pageNumber, element)
    else slotsRef.current.delete(pageNumber)
  }, [])

  const updateCurrentPage = useCallback(() => {
    const element = scrollRef.current
    if (!element) return
    const marker = element.scrollTop + element.clientHeight / 3
    let page = 1
    slotsRef.current.forEach((slot, pageNumber) => {
      if (slot.offsetTop <= marker && pageNumber > page) page = pageNumber
    })
    setCurrentPage(page)
  }, [])

  const frameRef = useRef(null)
  const onScroll = () => {
    if (frameRef.current) return
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null
      updateCurrentPage()
    })
  }
  useEffect(
    () => () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current)
    },
    [],
  )

  const scrollToPage = useCallback((pageNumber) => {
    const slot = slotsRef.current.get(pageNumber)
    if (slot && scrollRef.current) {
      scrollRef.current.scrollTop = slot.offsetTop - PAGE_GAP
    }
  }, [])

  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages)
    // Size the not-yet-rendered pages from the first one, so scrolling doesn't jump.
    pdf
      .getPage(1)
      .then((page) => {
        const viewport = page.getViewport({ scale: 1 })
        if (viewport.width > 0) setPageRatio(viewport.height / viewport.width)
      })
      .catch(() => {})
  }

  const status = (content) => (
    <div className='pdf-reader__status'>{content}</div>
  )

  return (
    <div
      className='pdf-reader'
      // Make saving and copying a little harder (there is no download button).
      onContextMenu={(event) => event.preventDefault()}
      onDragStart={(event) => event.preventDefault()}
    >
      {numPages > 0 && (
        <div className='pdf-reader__toolbar'>
          <button
            type='button'
            title='Previous page'
            onClick={() => scrollToPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </button>
          <span className='pdf-reader__page-count'>
            {currentPage} / {numPages}
          </span>
          <button
            type='button'
            title='Next page'
            onClick={() => scrollToPage(currentPage + 1)}
            disabled={currentPage >= numPages}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>

          <span className='pdf-reader__divider' />

          <button
            type='button'
            title='Zoom out'
            onClick={() => changeZoom(zoomIndex - 1)}
            disabled={zoomIndex === 0}
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
          <span className='pdf-reader__zoom'>{Math.round(zoom * 100)}%</span>
          <button
            type='button'
            title='Zoom in'
            onClick={() => changeZoom(zoomIndex + 1)}
            disabled={zoomIndex === ZOOM_STEPS.length - 1}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            type='button'
            title='Fit to width'
            onClick={() => changeZoom(ZOOM_STEPS.indexOf(1))}
            disabled={zoom === 1}
          >
            <FontAwesomeIcon icon={faCompress} />
          </button>
        </div>
      )}

      <div
        ref={setScrollElement}
        className='pdf-reader__scroll'
        onScroll={onScroll}
        aria-label={title || 'PDF document'}
      >
        {readableUrl && containerWidth > 0 && (
          <Document
            key={`${readableUrl}-${reloadKey}`}
            file={readableUrl}
            options={PDF_OPTIONS}
            onLoadSuccess={onDocumentLoadSuccess}
            onItemClick={({ pageNumber }) => scrollToPage(pageNumber)}
            loading={status('Loading PDF…')}
            error={status(
              <>
                <p className='mb-3'>This PDF couldn't be loaded.</p>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={() => setReloadKey((key) => key + 1)}
                >
                  Try again
                </button>
              </>,
            )}
            onLoadError={(error) => console.error('PDF load error', error)}
          >
            {Array.from({ length: numPages }, (_, index) => (
              <PageSlot
                key={index + 1}
                pageNumber={index + 1}
                width={pageWidth}
                height={pageHeight}
                scrollRoot={scrollRoot}
                registerSlot={registerSlot}
              />
            ))}
          </Document>
        )}
      </div>
    </div>
  )
}
